import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Avatar
} from '@mui/material';
import {
  Email,
  Lock,
  Send,
  Key as OTPKey,
  Visibility,
  VisibilityOff,
  LockOutlined
} from '@mui/icons-material';
import { adminAuthAPI } from '../../services/merchantAPI';

const AdminResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Extract email and OTP from URL query parameters
  const queryParams = new URLSearchParams(location.search);
  const initialEmail = queryParams.get('email') || '';
  const initialOtp = queryParams.get('otp') || '';

  const [formData, setFormData] = useState({
    email: initialEmail,
    otp: initialOtp,
    new_password: '',
    confirm_new_password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState({
    email: '',
    otp: '',
    new_password: '',
    confirm_new_password: '',
  });

  // Client-side validation functions
  const validateEmail = (email) => {
    if (!email) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return '';
  };

  const validateOTP = (otp) => {
    if (!otp) return 'OTP is required';
    if (otp.length < 4) return 'OTP must be at least 4 characters';
    return '';
  };

  const validatePassword = (password) => {
    if (!password) return 'Password is required';
    if (password.length < 6) return 'Password must be at least 6 characters long';
    return '';
  };

  const validateConfirmPassword = (password, confirmPassword) => {
    if (!confirmPassword) return 'Please confirm your password';
    if (password !== confirmPassword) return 'Passwords do not match';
    return '';
  };

  const validateField = (fieldName, value) => {
    switch (fieldName) {
      case 'email':
        return validateEmail(value);
      case 'otp':
        return validateOTP(value);
      case 'new_password':
        return validatePassword(value);
      case 'confirm_new_password':
        return validateConfirmPassword(formData.new_password, value);
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    setError('');
    setSuccess('');
    
    // Clear field errors when user types
    setFieldErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Client-side validation
    const emailError = validateEmail(formData.email);
    const otpError = validateOTP(formData.otp);
    const passwordError = validatePassword(formData.new_password);
    const confirmPasswordError = validateConfirmPassword(formData.new_password, formData.confirm_new_password);
    
    if (emailError || otpError || passwordError || confirmPasswordError) {
      setFieldErrors({
        email: emailError,
        otp: otpError,
        new_password: passwordError,
        confirm_new_password: confirmPasswordError,
      });
      setLoading(false);
      return;
    }

    try {
      const response = await adminAuthAPI.resetPassword({
        email: formData.email,
        otp: formData.otp,
        new_password: formData.new_password,
      });
      setSuccess(response.data.message || 'Password has been reset successfully!');
      setTimeout(() => {
        navigate('/glam-admin/login');
      }, 3000);
    } catch (err) {
      // Show all server errors as toast (general error)
      setError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 2,
            width: '100%'
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
            <LockOutlined />
          </Avatar>
          <Typography component="h1" variant="h5" sx={{ mb: 1, fontWeight: 500 }}>
            Reset Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            Enter your new password to complete the reset process.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
              disabled
              error={!!fieldErrors.email}
              helperText={fieldErrors.email}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: {
                    xs: '14px', // Smaller font size on mobile
                    sm: '16px'  // Normal font size on larger screens
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="otp"
              label="One-Time Password (OTP)"
              name="otp"
              value={formData.otp}
              onChange={handleChange}
              disabled
              error={!!fieldErrors.otp}
              helperText={fieldErrors.otp}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: {
                    xs: '14px', // Smaller font size on mobile
                    sm: '16px'  // Normal font size on larger screens
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <OTPKey color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="new_password"
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              id="new_password"
              autoComplete="new-password"
              value={formData.new_password}
              onChange={handleChange}
              disabled={loading}
              error={!!fieldErrors.new_password}
              helperText={fieldErrors.new_password}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: {
                    xs: '14px', // Smaller font size on mobile
                    sm: '16px'  // Normal font size on larger screens
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirm_new_password"
              label="Confirm New Password"
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirm_new_password"
              autoComplete="new-password"
              value={formData.confirm_new_password}
              onChange={handleChange}
              disabled={loading}
              error={!!fieldErrors.confirm_new_password}
              helperText={fieldErrors.confirm_new_password}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: {
                    xs: '14px', // Smaller font size on mobile
                    sm: '16px'  // Normal font size on larger screens
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: 3, 
                mb: 2, 
                py: 1.5,
                background: 'linear-gradient(90deg, #6318C6 0%, #F2CC00 100%)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #6318C6 0%, #F2CC00 100%)',
                }
              }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                <>
                  <Send sx={{ mr: 1 }} /> Reset Password
                </>
              )}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link 
                to="/glam-admin/login" 
                style={{ 
                  textDecoration: 'none', 
                  color: '#667eea',
                  fontSize: '14px'
                }}
              >
                Back to Login
              </Link>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default AdminResetPassword; 